import { useCallback, useEffect, useRef, useState } from 'react'
import {
    Text,
    TextInput,
    TouchableOpacity,
    View,
    ScrollView,
    Button,
    StyleSheet
} from 'react-native'
import { Formik } from 'formik'
import * as yup from 'yup'
import MaskInput, { Masks } from 'react-native-mask-input'
import { Feather } from '@expo/vector-icons'

// styles
import { globalStyles, colorPalette } from '../../../static/styles/global'

// i18n
import { en as formSelectEn } from '../../../i18n/FormSelect'
import { en as personalInfoEn } from '../../../i18n/PersonalInformationForms'

// components
import CustomButton from '../../shared/button/Button'
import Select from '../../shared/form/Select'
import Card from '../../shared/card/Card'
import CardSeparator from '../../shared/card/CardSeparator'
import {
    customMasks,
    deserializeDOB,
    getUnmaskedValue,
    mapSelectKey,
    setMaskedValue,
    validateEmail,
    validatePhone
} from '../../../utils'
import ScreenStatus from '../ScreenStatus'
import SelectKeyValue from '../../shared/form/SelectKeyValue'
import { Loading } from '../../shared/navigator/Loading'

// schema
const contactInformationSchema = yup.object({
    mainPhoneNumber: yup
        .string()
        .nullable()
        .required(personalInfoEn.contactInformation.validateMsg.phoneNumber)
        .test(
            'is-phone',
            personalInfoEn.contactInformation.validateMsg.phoneNumberFormat,
            (val) => {
                return validatePhone(val)
            }
        ),
    secondaryPhoneNumber: yup
        .string()
        .nullable()
        .test(
            'is-phone',
            personalInfoEn.contactInformation.validateMsg.phoneNumberFormat,
            (val) => {
                return !val || validatePhone(val)
            }
        ),
    employeeEmailAddress: yup.string().nullable(),
    // .trim()
    // .required(personalInfoEn.contactInformation.validateMsg.email)
    // .test(
    //   'is-email',
    //   personalInfoEn.contactInformation.validateMsg.emailFormat,
    //   (val) => {
    //     return validateEmail(val) ? true : false
    //   }
    // )
    emergencyContactName: yup.string().nullable(),
    emergencyPhoneNumber: yup
        .string()
        .nullable()
        .test(
            'is-phone',
            personalInfoEn.contactInformation.validateMsg.phoneNumberFormat,
            (val) => {
                return !val || validatePhone(val)
            }
        ),
    emergencyRelationshipValue: yup.string().nullable(),
    alternateEmergencyContactName: yup.string().nullable(),
    alternateEmergencyPhoneNumber: yup
        .string()
        .nullable()
        .test(
            'is-phone',
            personalInfoEn.contactInformation.validateMsg.phoneNumberFormat,
            (val) => {
                return !val || validatePhone(val)
            }
        ),
    alternateEmergencyRelationshipValue: yup.string().nullable()
})

// stores
import { usePersonalStore } from '../../../stores/personalInfo'

// types
import {
    PersonalStatuses,
    PersonalStatusesLocked,
    PersonalData,
    updatedpersonalData
} from '../../../types/personalTypes'
import HomeDetailsHeader from '../HomeDetailsHeader'
import PersonalContactInfoSkeleton from '../../../skeletons/PersonalContactInfoSkeleton'
//translation
import { useTranslation } from 'react-i18next'
import { useInitializeStore } from '../../../stores/initialize'
import { useFocusEffect } from '@react-navigation/native'
import Toast from 'react-native-toast-message'


// initial data
type InitialValues = Pick<
    updatedpersonalData,

    | 'firstName'
    | 'middleInitial'
    | 'lastName'
    | 'addressLine1'
    | 'addressLine2'
    | 'city'
    | 'stateValue'
    | 'zip'
    | 'dateOfBirth'
    | 'genderValue'
    | 'maritalStatusValue'
    | 'employeeDiscount'
    | 'mainPhoneNumber'
    | 'secondaryPhoneNumber'
    | 'emergencyContactName'
    | 'emergencyPhoneNumber'
    | 'emergencyRelationshipValue'
    | 'alternateEmergencyContactName'
    | 'alternateEmergencyPhoneNumber'
    | 'alternateEmergencyRelationshipValue'
>

const initialValues: InitialValues = {

    firstName: '',
    middleInitial: '',
    lastName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    stateValue: '',
    zip: '',
    dateOfBirth: '',
    genderValue: '',
    maritalStatusValue: '',
    employeeDiscount: null,
    mainPhoneNumber: '',
    secondaryPhoneNumber: '',
    emergencyContactName: '',
    emergencyPhoneNumber: '',
    emergencyRelationshipValue: '',
    alternateEmergencyContactName: '',
    alternateEmergencyPhoneNumber: '',
    alternateEmergencyRelationshipValue: ''
}

const UpdateContactInformationForm = ({ navigation, route }: any): React.ReactNode => {
    const { t } = useTranslation()
    const formikRef = useRef(null)
    const [status, setStatus] = useState<PersonalStatuses | null>(null)

    // params
    const { params } = route
    const { mostRecentUserOnBoardings: userId, relationships, basicInfoDto } = params

    // stores' data
    const { loading, error, personalData, getUpdatedData, updatedData, updatepersonalinformation, getDisablekeys, disablekeysData } =
        usePersonalStore()

    const [alternativeEmergencyContact, setAlternativeEmergencyContact] =
        useState<boolean>(false)

    const handleAddSecondaryEmergencyContact = () => {
        setAlternativeEmergencyContact(true)
    }

    const handleRemoveSecondaryEmergencyContact = (props: any) => {
        props.setFieldValue('alternateEmergencyContactName', '')
        props.setFieldValue('alternateEmergencyPhoneNumber', '')
        props.setFieldValue('alternateEmergencyRelationshipValue', '')
        setAlternativeEmergencyContact(false)
    }

    const intializestore = useInitializeStore()

    // useEffect(() => {
    //     getUpdatedData(intializestore.initializeData?.userkey)
    // }, [])

    useFocusEffect(
        useCallback(() => {
            getUpdatedData(intializestore.initializeData?.userkey);
            getDisablekeys(personalData?.entityId)
        }, [])
    );

    useEffect(() => {
        if (formikRef.current) {
            setMaskedValue('mainPhoneNumber', Masks.USA_PHONE, formikRef)
            setMaskedValue('secondaryPhoneNumber', Masks.USA_PHONE, formikRef)
            setMaskedValue('emergencyPhoneNumber', Masks.USA_PHONE, formikRef)
            setMaskedValue(
                'alternateEmergencyPhoneNumber',
                Masks.USA_PHONE,
                formikRef
            )

            if (
                formikRef.current.values.alternateEmergencyContactName ||
                formikRef.current.values.alternateEmergencyPhoneNumber ||
                formikRef.current.values.alternateEmergencyRelationshipValue
            ) {
                setAlternativeEmergencyContact(true)
            }
        }

        // if (personalData?.statusDescription)
        //     setStatus(personalData.statusDescription)

        return () => { }
    }, [formikRef.current, loading])

    function deepEqual(a: any, b: any) {
        const sortObject = obj =>
            Object.keys(obj)
                .sort()
                .reduce((result, key) => {
                    result[key] = obj[key];
                    return result;
                }, {});

        return JSON.stringify(sortObject(a)) === JSON.stringify(sortObject(b));
    }

    if (loading) return <PersonalContactInfoSkeleton />

    if (error) return <ScreenStatus message={error} />



    return (
        updatedData && (
            <>
                <View style={[globalStyles.primaryBgColor, globalStyles.containerBand]}>
                    <View style={[globalStyles.horizontalPadGap]}>
                        <HomeDetailsHeader
                            title={t('personalinformation')}
                            description={t('personaldescription')}
                        />
                    </View>
                </View>
                <View style={[globalStyles.containerOverlap]}>
                    <Formik
                        enableReinitialize
                        innerRef={formikRef}
                        initialValues={{
                            ...initialValues,
                            ...updatedData.data, ...basicInfoDto

                        }}
                        validationSchema={contactInformationSchema}
                        onSubmit={async (values, actions) => {


                            // TODO: call action to save values to DB - remove white spaces from values entries on frontend
                            const contactInfoDto = {
                                ...values,
                                mainPhoneNumber: getUnmaskedValue(
                                    values.mainPhoneNumber,
                                    Masks.USA_PHONE
                                ),
                                secondaryPhoneNumber: getUnmaskedValue(
                                    values.secondaryPhoneNumber,
                                    Masks.USA_PHONE
                                ),
                                emergencyPhoneNumber: getUnmaskedValue(
                                    values.emergencyPhoneNumber,
                                    Masks.USA_PHONE
                                ),
                                alternateEmergencyPhoneNumber: getUnmaskedValue(
                                    values.alternateEmergencyPhoneNumber,
                                    Masks.USA_PHONE
                                )
                            }

                            delete (contactInfoDto.entityId)
                            delete (contactInfoDto.essUserKey)
                            delete (contactInfoDto.sequence)
                            const newDto = updatedData.data
                            delete (newDto.entityId)
                            delete (newDto.essUserKey)
                            delete (newDto.sequence)
                            let finaldto = {
                                entityId: personalData?.entityId,
                                essUserKey: intializestore?.initializeData?.userkey,
                                storeId: personalData.storeEntityId,
                                userPersonalInfo: contactInfoDto
                            }

                            if (!deepEqual(contactInfoDto, newDto)) {
                                const data: any = await updatepersonalinformation(finaldto)
                                if (data?.success) {
                                    await Toast.show({
                                        type: 'success',
                                        position: 'bottom',
                                        text1: t(data?.message),
                                        visibilityTime: 3000,
                                        autoHide: true,
                                        topOffset: 60,
                                        onHide: () => {
                                            navigation.replace('LabourScreen')

                                        }
                                    })
                                }
                                else {
                                    await Toast.show({
                                        type: 'error',
                                        position: 'bottom',
                                        text1: t(data?.message),
                                        visibilityTime: 3000,
                                        autoHide: true,
                                        topOffset: 60
                                    })
                                }
                            }
                            else {
                                await Toast.show({
                                    type: 'error',
                                    position: 'bottom',
                                    text1: t("There is no change in the data to submit"),
                                    visibilityTime: 3000,
                                    autoHide: true,
                                    topOffset: 60,

                                })
                            }

                        }}
                    >
                        {(props) => (
                            <View>
                                <Card>
                                    <View
                                        style={[
                                            globalStyles.hDirection,
                                            globalStyles.vCenterItems,
                                            globalStyles.hBetweenItems,
                                            globalStyles.flexWrapped
                                        ]}
                                    >
                                        <Text
                                            style={[
                                                globalStyles.text,
                                                globalStyles.fwBold,
                                                globalStyles.fs18,
                                                globalStyles.blackTextColor
                                            ]}
                                        >
                                            {t('contactinformation')}
                                        </Text>
                                        <Text
                                            style={[
                                                globalStyles.text,
                                                globalStyles.fs14,
                                                globalStyles.grayTextColor,
                                                globalStyles.pt2
                                            ]}
                                        >
                                            <Text style={styles.required}>*{t('requiredfield')}</Text>
                                        </Text>
                                    </View>
                                    <CardSeparator light={true} />
                                    <View>
                                        {/* <Text>{JSON.stringify(props, null, 2)}</Text> */}
                                        <View style={[globalStyles.mb2]}>
                                            <Text
                                                style={[
                                                    globalStyles.inputLabel,
                                                    globalStyles.inputLabelLarge,
                                                    globalStyles.inputLabelPrimary
                                                ]}
                                            >
                                                {t('mainphonenumber')}<Text style={styles.required}>*</Text>
                                            </Text>
                                            <MaskInput
                                                style={[
                                                    globalStyles.input,
                                                    globalStyles.inputLight,
                                                    disablekeysData?.mainPhoneNumber === 0 &&
                                                    globalStyles.inputReadOnly,
                                                    props.touched.mainPhoneNumber &&
                                                    props.errors.mainPhoneNumber &&
                                                    globalStyles.inputError
                                                ]}
                                                value={props.values.mainPhoneNumber || ''}
                                                onChangeText={props.handleChange('mainPhoneNumber')}
                                                onBlur={props.handleBlur('mainPhoneNumber')}
                                                mask={Masks.USA_PHONE}
                                                keyboardType="phone-pad"
                                                editable={disablekeysData?.mainPhoneNumber == 1}
                                            />
                                            {props.touched.mainPhoneNumber &&
                                                props.errors.mainPhoneNumber && (
                                                    <Text style={globalStyles.dangerAlert}>
                                                        {props.errors.mainPhoneNumber as string}
                                                    </Text>
                                                )}
                                        </View>
                                        <View style={[globalStyles.mb2]}>
                                            <Text
                                                style={[
                                                    globalStyles.inputLabel,
                                                    globalStyles.inputLabelLarge,
                                                    globalStyles.inputLabelPrimary
                                                ]}
                                            >
                                                {t('secondaryphonenumber')}
                                            </Text>
                                            <MaskInput
                                                style={[
                                                    globalStyles.input,
                                                    globalStyles.inputLight,
                                                    disablekeysData?.secondaryPhoneNumber === 0 &&
                                                    globalStyles.inputReadOnly,
                                                    props.touched.secondaryPhoneNumber &&
                                                    props.errors.secondaryPhoneNumber &&
                                                    globalStyles.inputError
                                                ]}
                                                value={props.values.secondaryPhoneNumber || ''}
                                                onChangeText={props.handleChange(
                                                    'secondaryPhoneNumber'
                                                )}
                                                onBlur={props.handleBlur('secondaryPhoneNumber')}
                                                mask={Masks.USA_PHONE}
                                                keyboardType="phone-pad"
                                                editable={disablekeysData?.secondaryPhoneNumber == 1}
                                            />
                                            {props.touched.secondaryPhoneNumber &&
                                                props.errors.secondaryPhoneNumber && (
                                                    <Text style={globalStyles.dangerAlert}>
                                                        {props.errors.secondaryPhoneNumber as string}
                                                    </Text>
                                                )}
                                        </View>
                                        <View>
                                            <View style={[globalStyles.mb2]}>
                                                <Text
                                                    style={[
                                                        globalStyles.inputLabel,
                                                        globalStyles.inputLabelLarge,
                                                        globalStyles.inputLabelPrimary
                                                    ]}
                                                >
                                                    {t('emergencycontact')}
                                                </Text>
                                                <TextInput
                                                    style={[
                                                        globalStyles.input,
                                                        globalStyles.inputLight,
                                                        disablekeysData?.emergencyContactName === 0 &&
                                                        globalStyles.inputReadOnly, { textTransform: 'uppercase' }
                                                    ]}
                                                    placeholder={''}
                                                    onChangeText={props.handleChange(
                                                        'emergencyContactName'
                                                    )}
                                                    value={props.values.emergencyContactName || ''}
                                                    onBlur={props.handleBlur('emergencyContactName')}
                                                    editable={disablekeysData?.emergencyContactName == 1}
                                                />
                                            </View>
                                            <View style={[globalStyles.mb2]}>
                                                <Text
                                                    style={[
                                                        globalStyles.inputLabel,
                                                        globalStyles.inputLabelLarge,
                                                        globalStyles.inputLabelPrimary
                                                    ]}
                                                >
                                                    {t('emergencyphonenumber')}
                                                </Text>
                                                <MaskInput
                                                    style={[
                                                        globalStyles.input,
                                                        globalStyles.inputLight,
                                                        disablekeysData?.emergencyPhoneNumber === 0 &&
                                                        globalStyles.inputReadOnly,
                                                        props.touched.emergencyPhoneNumber &&
                                                        props.errors.emergencyPhoneNumber &&
                                                        globalStyles.inputError
                                                    ]}
                                                    onChangeText={props.handleChange(
                                                        'emergencyPhoneNumber'
                                                    )}
                                                    value={props.values.emergencyPhoneNumber || ''}
                                                    onBlur={props.handleBlur('emergencyPhoneNumber')}
                                                    mask={Masks.USA_PHONE}
                                                    keyboardType="phone-pad"
                                                    editable={disablekeysData?.emergencyPhoneNumber == 1}
                                                />
                                                {props.touched.emergencyPhoneNumber &&
                                                    props.errors.emergencyPhoneNumber && (
                                                        <Text style={globalStyles.dangerAlert}>
                                                            {props.errors.emergencyPhoneNumber as string}
                                                        </Text>
                                                    )}
                                            </View>
                                            <View style={[globalStyles.mb2]}>
                                                <Text
                                                    style={[
                                                        globalStyles.inputLabel,
                                                        globalStyles.inputLabelLarge,
                                                        globalStyles.inputLabelPrimary
                                                    ]}
                                                >
                                                    {t('emergencyrelation')}
                                                </Text>
                                                <SelectKeyValue
                                                    title={'Contact Relationship'}
                                                    options={relationships}
                                                    selectedOption={mapSelectKey(
                                                        relationships,
                                                        personalData.emergencyRelationshipValue
                                                    )}
                                                    onSelect={(option: string) => {
                                                        props.setFieldValue(
                                                            'emergencyRelationshipValue',
                                                            option
                                                        )
                                                    }}
                                                    isDisabled={disablekeysData?.emergencyRelationshipValue == 0}
                                                />
                                            </View>
                                        </View>
                                        {/* emergency contact */}
                                        {alternativeEmergencyContact && (
                                            <View>
                                                <View
                                                    style={{
                                                        borderBottomColor: '#EEEEEE',
                                                        borderBottomWidth: 1,
                                                        marginTop: 15,
                                                        marginBottom: 20
                                                    }}
                                                />
                                                <View style={[globalStyles.mb4]}>
                                                    <Text
                                                        style={[
                                                            globalStyles.text,
                                                            globalStyles.fwBold,
                                                            globalStyles.fs14,
                                                            globalStyles.primaryTextColor
                                                        ]}
                                                    >
                                                        {t('alternative')}
                                                    </Text>
                                                </View>
                                                {/* separator */}
                                                <View style={[globalStyles.mb2]}>
                                                    <Text
                                                        style={[
                                                            globalStyles.inputLabel,
                                                            globalStyles.inputLabelLarge,
                                                            globalStyles.inputLabelPrimary
                                                        ]}
                                                    >
                                                        {t('emergencycontact')}
                                                    </Text>
                                                    <TextInput
                                                        style={[
                                                            globalStyles.input,
                                                            globalStyles.inputLight,
                                                            disablekeysData?.alternateEmergencyContactName === 0 &&
                                                            globalStyles.inputReadOnly, { textTransform: 'uppercase' }
                                                        ]}
                                                        placeholder={''}
                                                        onChangeText={props.handleChange(
                                                            'alternateEmergencyContactName'
                                                        )}
                                                        value={
                                                            props.values.alternateEmergencyContactName || ''
                                                        }
                                                        onBlur={props.handleBlur(
                                                            'alternateEmergencyContactName'
                                                        )}
                                                        editable={disablekeysData?.alternateEmergencyContactName == 1}
                                                    />
                                                </View>
                                                <View style={[globalStyles.mb2]}>
                                                    <Text
                                                        style={[
                                                            globalStyles.inputLabel,
                                                            globalStyles.inputLabelLarge,
                                                            globalStyles.inputLabelPrimary
                                                        ]}
                                                    >
                                                        {t('emergencyphonenumber')}
                                                    </Text>
                                                    <MaskInput
                                                        style={[
                                                            globalStyles.input,
                                                            globalStyles.inputLight,
                                                            disablekeysData?.alternateEmergencyPhoneNumber === 0 &&
                                                            globalStyles.inputReadOnly,
                                                            props.touched.alternateEmergencyPhoneNumber &&
                                                            props.errors.alternateEmergencyPhoneNumber &&
                                                            globalStyles.inputError
                                                        ]}
                                                        onChangeText={props.handleChange(
                                                            'alternateEmergencyPhoneNumber'
                                                        )}
                                                        value={
                                                            props.values.alternateEmergencyPhoneNumber || ''
                                                        }
                                                        onBlur={props.handleBlur(
                                                            'alternateEmergencyPhoneNumber'
                                                        )}
                                                        mask={Masks.USA_PHONE}
                                                        keyboardType="phone-pad"
                                                        editable={disablekeysData?.alternateEmergencyPhoneNumber == 1}
                                                    />
                                                    {props.touched.alternateEmergencyPhoneNumber &&
                                                        props.errors.alternateEmergencyPhoneNumber && (
                                                            <Text style={globalStyles.dangerAlert}>
                                                                {props.errors.alternateEmergencyPhoneNumber as string}
                                                            </Text>
                                                        )}
                                                </View>
                                                <View style={[globalStyles.mb2]}>
                                                    <Text
                                                        style={[
                                                            globalStyles.inputLabel,
                                                            globalStyles.inputLabelLarge,
                                                            globalStyles.inputLabelPrimary
                                                        ]}
                                                    >
                                                        {t('emergencyrelation')}
                                                    </Text>
                                                    <SelectKeyValue
                                                        title={'Contact Relationship'}
                                                        options={relationships}
                                                        selectedOption={mapSelectKey(
                                                            relationships,
                                                            personalData.alternateEmergencyRelationshipValue
                                                        )}
                                                        onSelect={(option: string) => {
                                                            props.setFieldValue(
                                                                'alternateEmergencyRelationshipValue',
                                                                option
                                                            )
                                                        }}
                                                        isDisabled={disablekeysData?.alternateEmergencyRelationshipValue == 0}
                                                    />
                                                </View>
                                            </View>
                                        )}
                                        {/* alternative emergency contact */}

                                        <View style={[globalStyles.mt1, globalStyles.mb1]}>
                                            {!alternativeEmergencyContact
                                                ? (
                                                    <TouchableOpacity
                                                        onPress={handleAddSecondaryEmergencyContact}
                                                    >
                                                        <Text
                                                            style={[
                                                                globalStyles.text,
                                                                globalStyles.fs14,
                                                                globalStyles.primaryTextColor,
                                                                globalStyles.textUnderline
                                                            ]}
                                                        >
                                                            +{t('addemergency')}
                                                        </Text>
                                                    </TouchableOpacity>
                                                )
                                                : (
                                                    <TouchableOpacity
                                                        onPress={() =>
                                                            handleRemoveSecondaryEmergencyContact(props)
                                                        }
                                                    >
                                                        <Text
                                                            style={[
                                                                globalStyles.text,
                                                                globalStyles.fs14,
                                                                globalStyles.dangerTextColor,
                                                                globalStyles.textUnderline
                                                            ]}
                                                        >
                                                            x{t('remove')}
                                                        </Text>
                                                    </TouchableOpacity>
                                                )}
                                        </View>
                                        {/* add / remove secondary emergency contact */}
                                    </View>
                                </Card>
                                <View
                                    style={{
                                        marginTop: 10,
                                        marginBottom: 25,
                                        flexDirection: 'row',
                                        justifyContent: 'space-between'
                                    }}
                                >
                                    <CustomButton
                                        text={
                                            // PersonalStatusesLocked.includes(status)
                                            //     ? t('previous'):
                                            t('previous')
                                        }
                                        color={'warn'}
                                        onPress={() => {
                                            navigation.navigate('UpdatePersonalBasicInformationScreen')
                                        }}
                                    />

                                    <CustomButton
                                        text={
                                            // PersonalStatusesLocked.includes(status)
                                            //     ? t('home'):
                                            t('submitinfo')
                                        }
                                        onPress={() =>
                                            // PersonalStatusesLocked.includes(status)
                                            //     ? navigation.navigate('Onboarding') // Replace 'Home' with your actual screen name : 
                                            props.handleSubmit()
                                        }
                                        color='success'
                                    />
                                </View>
                            </View>
                        )}
                    </Formik>

                </View>
            </>
        )
    )
}

const styles = StyleSheet.create({
    required: {
        color: "red"
    }
})

export default UpdateContactInformationForm
